/**
 * Design B motion core, "Cinematic Clinic".
 *
 * GSAP, ScrollTrigger, SplitText and Lenis load as classic deferred UMD
 * scripts from snippets/clinic-scripts.liquid, so they are on window by the
 * time any module runs. Everything here degrades to the final, static state:
 * reduced motion, design mode, a blocked script or a low-power phone all get
 * a complete page with nothing hidden.
 *
 * The one Horizon fact everything hangs on: at 990px and up the document does
 * not scroll. `html` and `body` are overflow: hidden and `.page-wrapper` is the
 * scroller (assets/base.css, assets/scroll-container.js). Below 990px the
 * window scrolls so the mobile address bar can collapse. Every ScrollTrigger is
 * therefore given its scroller explicitly, per breakpoint, via scene().
 */
import { isLowPowerDevice } from '@theme/utilities';

const html = document.documentElement;
const { gsap, ScrollTrigger, SplitText, Lenis } = window;

export const DESKTOP = '(min-width: 990px)';
const REDUCE = '(prefers-reduced-motion: reduce)';
const reduceQuery = matchMedia(REDUCE);
const designMode = html.classList.contains('shopify-design-mode') || Boolean(window.Shopify?.designMode);

/** Data saver or a weak phone: posters instead of ambient video, slider instead of WebGL. */
export const lite = navigator.connection?.saveData === true || isLowPowerDevice();

/** True when GSAP is present and we are not inside the theme editor. */
export const motionEnabled = Boolean(gsap && ScrollTrigger) && !designMode;

export const reducedMotion = () => reduceQuery.matches;

/**
 * anticipatePin for a desktop pin. Native scroll runs on its own thread, so a
 * pin there is applied a frame early to avoid a jolt. Lenis moves the page and
 * ScrollTrigger in the same tick, and anticipating there locks the pin early,
 * which reads as a small snap as the section settles.
 */
export const pinAnticipation = () => (window.clinicLenis ? 0 : 1);

export function pageScroller() {
  return matchMedia(DESKTOP).matches ? document.querySelector('.page-wrapper') || window : window;
}

if (motionEnabled) {
  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);
  ScrollTrigger.config({ ignoreMobileResize: true });
  gsap.defaults({ ease: 'expo.out', duration: 1.1 });
}

/* ------------------------------------------------------------------ */
/* Smooth scroll: desktop, fine pointer, motion allowed, not low power. */
/* Touch keeps native scrolling; in-app browsers fight hijacked scroll. */
/* ------------------------------------------------------------------ */
if (motionEnabled && Lenis && !isLowPowerDevice()) {
  gsap.matchMedia().add(`${DESKTOP} and (hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)`, () => {
    const wrapper = document.querySelector('.page-wrapper');
    if (!wrapper) return;

    // Content is the wrapper itself: its scrollHeight spans header, main and
    // footer. #MainContent alone would cap the limit above the footer. Size
    // changes are fed in by refreshSoon(), which calls lenis.resize().
    const lenis = new Lenis({
      wrapper,
      content: wrapper,
      autoRaf: false,
      anchors: true,
      lerp: 0.11,
      wheelMultiplier: 0.95,
      syncTouch: false,
      prevent: (node) =>
        Boolean(node.closest?.('[data-lenis-prevent], dialog, .menu-drawer, .menu-list__submenu, .clinic-services__panel, .clinic-story-viewer')),
    });
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    lenis.on('scroll', ScrollTrigger.update);

    // Horizon locks scroll with html[scroll-lock] for every dialog and drawer.
    const syncLock = () => (html.hasAttribute('scroll-lock') ? lenis.stop() : lenis.start());
    const lockObserver = new MutationObserver(syncLock);
    lockObserver.observe(html, { attributes: true, attributeFilter: ['scroll-lock'] });
    html.classList.add('clinic-lenis');
    window.clinicLenis = lenis;

    return () => {
      lockObserver.disconnect();
      gsap.ticker.remove(tick);
      lenis.destroy();
      html.classList.remove('clinic-lenis');
      window.clinicLenis = null;
    };
  });
}

/* ------------------------------------------------------------------ */
/* Refresh, debounced, never while a drawer holds the scroll lock.      */
/* ------------------------------------------------------------------ */
let refreshTimer;
export function refreshSoon(delay = 150) {
  if (!motionEnabled) return;
  clearTimeout(refreshTimer);
  refreshTimer = setTimeout(() => {
    if (html.hasAttribute('scroll-lock')) return refreshSoon(400);
    window.clinicLenis?.resize();
    ScrollTrigger.refresh();
  }, delay);
}

if (motionEnabled) {
  const main = document.getElementById('MainContent');
  let lastHeight = 0;
  if (main && 'ResizeObserver' in window) {
    new ResizeObserver(() => {
      if (main.offsetHeight !== lastHeight) {
        lastHeight = main.offsetHeight;
        refreshSoon();
      }
    }).observe(main);
  }
  document.fonts?.ready.then(() => refreshSoon(50));
  addEventListener('pageshow', (event) => event.persisted && refreshSoon());
  addEventListener('load', () => refreshSoon(50));
  ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach((name) =>
    document.addEventListener(name, () => refreshSoon())
  );
}

/**
 * One motion scene per section custom element. `build` runs once per
 * breakpoint and gets the right scroller; whatever it creates is reverted
 * when the breakpoint flips, motion preferences change, or the section is
 * removed (call the returned function from disconnectedCallback).
 *
 * @param {Element} host
 * @param {(ctx: {scroller: Element|Window, desktop: boolean, pin: boolean}) => (void|Function)} build
 */
export function scene(host, build) {
  if (!motionEnabled) {
    revealStatic(host);
    return () => {};
  }
  // Unscoped on purpose: a scoped context makes SplitText resolve elements
  // as selector strings (GSAP 3.15). Every caller passes elements anyway.
  const mm = gsap.matchMedia();
  mm.add({ desktop: DESKTOP, reduce: REDUCE }, (context) => {
    const { desktop, reduce } = context.conditions;
    if (reduce) {
      revealStatic(host);
      return;
    }
    const scroller = desktop ? document.querySelector('.page-wrapper') || window : window;
    return build({ scroller, desktop, pin: desktop });
  });
  refreshSoon();
  return () => {
    mm.revert();
    refreshSoon();
  };
}

/** Settles everything in its final state: split headings and intro elements. */
function revealStatic(root) {
  root.querySelectorAll('[data-split]').forEach((el) => el.setAttribute('data-split-ready', ''));
  root.querySelectorAll('[data-intro], [data-intro-scale]').forEach((el) => {
    el.style.animation = 'none';
    el.style.opacity = '1';
    el.style.transform = 'none';
  });
}

/* ------------------------------------------------------------------ */
/* Reusable reveals.                                                    */
/* ------------------------------------------------------------------ */

/**
 * Line-by-line masked rise for a heading. Re-splits itself on resize and
 * after fonts load (autoSplit), keeps the heading readable to screen readers.
 */
export function splitReveal(el, { scroller, start = 'top 88%', delay = 0, stagger = 0.09, immediate = false } = {}) {
  if (!el) return;
  if (!SplitText) {
    el.setAttribute('data-split-ready', '');
    return;
  }
  return SplitText.create(el, {
    type: 'lines',
    mask: 'lines',
    linesClass: 'clinic-line',
    autoSplit: true,
    aria: 'auto',
    onSplit(self) {
      el.setAttribute('data-split-ready', '');
      return gsap.from(self.lines, {
        yPercent: 108,
        duration: 1.25,
        ease: 'expo.out',
        stagger,
        delay,
        scrollTrigger: immediate ? undefined : { trigger: el, start, scroller, once: true },
      });
    },
  });
}

/**
 * Wires the generic attributes inside a section:
 *   [data-split]          masked line reveal (headings)
 *   [data-rise]           fade and rise, batched
 *   [data-unveil]         media curtain, clip-path from the bottom edge
 *   [data-depth="0.12"]   parallax drift on scroll, fraction of own height
 */
export function revealAll(root, { scroller }) {
  root.querySelectorAll('[data-split]').forEach((el) => {
    if (!el.hasAttribute('data-split-manual')) splitReveal(el, { scroller, delay: Number(el.dataset.delay || 0) });
  });

  const risers = [...root.querySelectorAll('[data-rise]')];
  if (risers.length) {
    gsap.set(risers, { autoAlpha: 0, y: 28 });
    ScrollTrigger.batch(risers, {
      scroller,
      start: 'top 90%',
      once: true,
      onEnter: (batch) =>
        gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1, ease: 'expo.out', stagger: 0.08, overwrite: true }),
    });
  }

  root.querySelectorAll('[data-unveil]').forEach((el) => {
    gsap.fromTo(
      el,
      { clipPath: 'inset(18% 6% 18% 6% round var(--brand-radius-media))' },
      {
        clipPath: 'inset(0% 0% 0% 0% round var(--brand-radius-media))',
        ease: 'none',
        scrollTrigger: { trigger: el, scroller, start: 'top 95%', end: 'top 40%', scrub: 0.6 },
      }
    );
  });

  root.querySelectorAll('[data-depth]').forEach((el) => {
    const depth = Number(el.dataset.depth) || 0.1;
    gsap.fromTo(
      el,
      { yPercent: -depth * 50 },
      {
        yPercent: depth * 50,
        ease: 'none',
        scrollTrigger: { trigger: el.parentElement || el, scroller, start: 'top bottom', end: 'bottom top', scrub: true },
      }
    );
  });
}

/* ------------------------------------------------------------------ */
/* Ambient video: muted loops that only download and play in view.     */
/* ------------------------------------------------------------------ */

const ambientObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach(({ target, isIntersecting }) => {
            if (isIntersecting) {
              loadVideo(target);
              target.play().catch(() => {});
            } else if (!target.paused) {
              target.pause();
            }
          });
        },
        { rootMargin: '150px 0px', threshold: 0.01 }
      )
    : null;

/*
 * Shopify lists only the 1080p MP4 on a video object, but it encodes 720p
 * and 480p too: they are named in the video's HLS playlist and exist as
 * MP4s beside the 1080p file. These ambient loops never show larger than
 * about 700px, so a phone takes the 480p file and a desktop the 720p one
 * (for one clip: 3.0 MB and 8.3 MB instead of 13 MB). If the playlist
 * cannot be read, the original file plays.
 */
const SHOPIFY_VIDEO = /\/videos\/c\/vp\/([0-9a-f]+)\/\1\.(?:HD|SD)-\d+p-[\d.]+Mbps-\d+\.mp4/;

async function lighterSource(src, maxHeight) {
  const match = src.match(SHOPIFY_VIDEO);
  if (!match) return src;
  const id = match[1];
  const base = src.slice(0, src.indexOf(`${id}/`) + id.length + 1);
  const query = src.includes('?') ? src.slice(src.indexOf('?')) : '';
  try {
    const lines = (await (await fetch(`${base}${id}.m3u8`)).text()).split('\n');
    let best = null;
    lines.forEach((line, i) => {
      const res = line.match(/RESOLUTION=\d+x(\d+)/);
      const name = res && lines[i + 1] ? lines[i + 1].trim() : '';
      const q = name.match(/\.((?:HD|SD)-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_\.m3u8$/);
      if (!q) return;
      const height = Number(res[1]);
      if (height <= maxHeight && (!best || height > best.height)) {
        best = { height, file: `${id}.${q[1]}-${q[2]}-${q[3]}.mp4` };
      }
    });
    return best ? `${base}${best.file}${query}` : src;
  } catch (error) {
    return src;
  }
}

function inView(video) {
  const r = video.getBoundingClientRect();
  return r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;
}

/** Sets the src from data attributes, choosing the lighter file on phones. */
export function loadVideo(video) {
  if (!video || video.dataset.loaded) return false;
  const src = (innerWidth < 750 && video.dataset.srcMobile) || video.dataset.src;
  if (!src) return false;
  video.dataset.loaded = 'true';
  // The poster stays on screen until real frames are moving; the video
  // fades in over it (.clinic-media video[data-playing]).
  video.addEventListener('playing', () => video.setAttribute('data-playing', ''), { once: true });
  if (!SHOPIFY_VIDEO.test(src)) {
    video.src = src;
    return true;
  }
  lighterSource(src, innerWidth < 750 ? 480 : 720).then((light) => {
    video.src = light;
    // The caller's play() ran before the source existed; play now if the
    // clip is still meant to be showing (in view, and its door active).
    const door = video.closest('[data-door], .clinic-hero__door');
    if (inView(video) && (!door || door.hasAttribute('data-active'))) video.play().catch(() => {});
  });
  return true;
}

/** True when ambient video is allowed at all on this device and preference. */
export function ambientAllowed() {
  return !lite && !reducedMotion() && !designMode;
}

export function ambient(video) {
  if (!video || video.dataset.ambientBound) return;
  video.dataset.ambientBound = 'true';
  if (!ambientAllowed() || !ambientObserver) return;
  ambientObserver.observe(video);
}

export function releaseAmbient(video) {
  if (video && ambientObserver) ambientObserver.unobserve(video);
}

/* ------------------------------------------------------------------ */
/* Generic section element: reveals only, for sections without a      */
/* bespoke scene.                                                      */
/* ------------------------------------------------------------------ */

if (!customElements.get('clinic-section')) {
  customElements.define(
    'clinic-section',
    class ClinicSection extends HTMLElement {
      connectedCallback() {
        this.querySelectorAll('video[data-clinic-ambient]').forEach(ambient);
        this.dispose = scene(this, ({ scroller }) => revealAll(this, { scroller }));
      }
      disconnectedCallback() {
        this.dispose?.();
        this.querySelectorAll('video[data-clinic-ambient]').forEach(releaseAmbient);
      }
    }
  );
}

/**
 * Plays an intro on elements whose starting state comes from CSS
 * ([data-intro] = faded and lowered, [data-intro-scale] = scaled up).
 * Clears the CSS failsafe first so the two never fight.
 */
export function intro(targets, vars = {}) {
  const els = gsap.utils.toArray(targets);
  if (!els.length) return;
  els.forEach((el) => (el.style.animation = 'none'));
  if (vars.scale !== undefined) {
    const { scale, ...rest } = vars;
    return gsap.fromTo(els, { scale }, { scale: 1, duration: 2, ease: 'expo.out', ...rest });
  }
  return gsap.fromTo(els, { autoAlpha: 0, y: 18 }, { autoAlpha: 1, y: 0, duration: 1.1, ease: 'expo.out', ...vars });
}

/** Counts a number in an element from its current value to `to`. */
export function countTo(el, to, { duration = 0.9, format = (n) => Math.round(n).toLocaleString('en-US') } = {}) {
  if (!el) return;
  const from = Number(String(el.dataset.value ?? el.textContent).replace(/[^\d.]/g, '')) || 0;
  el.dataset.value = String(to);
  if (!motionEnabled || reducedMotion()) {
    el.textContent = format(to);
    return;
  }
  const state = { n: from };
  gsap.to(state, {
    n: to,
    duration,
    ease: 'expo.out',
    overwrite: true,
    onUpdate: () => (el.textContent = format(state.n)),
  });
}

export { gsap, ScrollTrigger };
